import {AVAILABILITY_LABELS, CREDIT_PACKS, CREDITS_CLASSES, LADDER_FOOTNOTE} from "pages/Front/Booking/Credits/CreditCalculator/constants";
import {CreditPack} from "pages/Front/Booking/Credits/CreditCalculator/types";
import {formatChf, formatPricePerCredit, rideRange} from "pages/Front/Booking/Credits/CreditCalculator/utils";
import {CheckBadgeMark} from "pages/Front/Booking/Credits/CreditCalculator/components/icons";
import {TicketChip} from "pages/Front/Booking/Credits/CreditCalculator/components/TicketChip";
import React from "react";

interface PackLadderProps {
    /** Credits of the currently recommended pack, if any. */
    highlightedPackCredits: number | null;
}

interface PackRowProps {
    pack: CreditPack;
    highlighted: boolean;
}

const PACK_GRID_COLUMNS =
    "grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,0.6fr)_minmax(0,1.25fr)] " +
    "sm:grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,1.25fr)] " +
    "md:grid-cols-[minmax(0,1.3fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.25fr)]";

function PackRow({pack, highlighted}: PackRowProps) {
    const range = rideRange(pack);
    const rowClasses = highlighted ? `${CREDITS_CLASSES.bgTint} ${CREDITS_CLASSES.border} border border-solid` : "border border-line bg-white";
    const primaryText = highlighted ? CREDITS_CLASSES.textStrong : "text-ink";
    const secondaryText = highlighted ? CREDITS_CLASSES.text : "text-ink-muted";

    return (
        <div role="row" className={`grid ${PACK_GRID_COLUMNS} ${rowClasses} rounded-lg`}>
            <div role="cell" className={`px-2 sm:px-3 py-2 text-xxs sm:text-sm font-semibold ${primaryText}`}>
                <span className="inline-flex items-center gap-1.5">
                    {highlighted && <CheckBadgeMark size={16} />}
                    {pack.credits} Credits
                    {highlighted && <span className="sr-only"> (Empfehlung)</span>}
                </span>
            </div>
            <div role="cell" className={`px-2 sm:px-3 py-2 text-xs sm:text-sm ${primaryText}`}>
                {formatChf(pack.priceChf)}
            </div>
            <div role="cell" className="px-2 sm:px-3 py-2 text-xxs sm:text-xs text-green-700">
                −{pack.discountLabel}
            </div>
            <div role="cell" className={`hidden px-2 sm:px-3 py-2 text-xxs sm:text-xs sm:block ${secondaryText}`}>
                {formatPricePerCredit(pack)}
            </div>
            <div role="cell" className={`hidden px-2 sm:px-3 py-2 text-xxs sm:text-xs md:block ${secondaryText}`}>
                {range.min}–{range.max} Fahrten
            </div>
            <div role="cell" className="px-2 sm:px-3 py-2 text-right">
                <TicketChip label={AVAILABILITY_LABELS[pack.availability]} />
            </div>
        </div>
    );
}

export function PackLadder({highlightedPackCredits}: PackLadderProps) {
    return (
        <section aria-labelledby="pack-ladder-heading">
            <h3 id="pack-ladder-heading" className="mb-2 text-sm font-semibold text-ink">
                Alle Pakete
            </h3>
            <div role="table" aria-label="Verfügbare Credit-Pakete" className="w-full">
                <div role="rowgroup">
                    <div role="row" className={`grid ${PACK_GRID_COLUMNS} text-left text-[10px] uppercase tracking-wide text-ink-muted`}>
                        <div role="columnheader" className="px-2 sm:px-3 pb-1 font-medium">
                            Paket
                        </div>
                        <div role="columnheader" className="px-2 sm:px-3 pb-1 font-medium">
                            Preis
                        </div>
                        <div role="columnheader" className="px-2 sm:px-3 pb-1 font-medium">
                            Rabatt
                        </div>
                        <div role="columnheader" className="hidden px-2 sm:px-3 pb-1 font-medium sm:block">
                            Pro Credit
                        </div>
                        <div role="columnheader" className="hidden px-2 sm:px-3 pb-1 font-medium md:block">
                            Reicht für
                        </div>
                        <div role="columnheader" className="px-2 sm:px-3 pb-1 text-right font-medium">
                            Verfügbar mit
                        </div>
                    </div>
                </div>
                <div role="rowgroup" className="space-y-1">
                    {CREDIT_PACKS.map(pack => (
                        <PackRow key={pack.credits} pack={pack} highlighted={pack.credits === highlightedPackCredits} />
                    ))}
                </div>
            </div>
            <p className="mt-1 text-xs text-ink-muted">{LADDER_FOOTNOTE}</p>
        </section>
    );
}
